import type { ReactNode } from 'react';
import type { TenantStatus } from '@yourstore/domain';
import { Chip, StageTrack } from '../kit';
import type { StageStep } from '../kit';
import { useSupportLook, SupportLookNotice } from './SupportLook';
import { useStageMenu } from './StageMenu';
import { statusMeaning } from '../../vocabulary';
import { useT } from '../../messages';
import type { T } from '../../messages';

/**
 * **Who this is, and where they are in getting live** — the top of a business
 * page (console redesign, phase A; plan §1, principle 1).
 *
 * It replaces the Lifecycle card, the Support card and the id-as-title header,
 * and answers in the order an operator asks: the business's **name** as the
 * title (the id beneath it, small and in mono, for copying into a terminal);
 * where it is (its timezone's city and abbreviation — the tenant record has no
 * address, and the timezone is what its hours are computed in); whether it is
 * **protected** (migration 0047's flag, which until now no screen showed); and
 * two actions — look at their screen, or move their stage.
 *
 * Under it, the stage track: Setting up → Moving their book in → Live →
 * Taking bookings online. The last is a readiness milestone rather than a
 * status (plan §2); nothing computes it yet, so it is drawn as "Later" and
 * never as reached — phase B1's readiness read is what will light it. Paused
 * and offboarded are states off the track: the track shows no current step and
 * the header says which with a chip.
 */
export function BusinessHeader({
  tenantId,
  name,
  timezone,
  status,
  isProtected,
  onChanged,
}: {
  tenantId: string;
  name: string;
  timezone: string;
  status: TenantStatus;
  isProtected: boolean;
  onChanged: () => void;
}): ReactNode {
  const t = useT();
  const support = useSupportLook(tenantId);
  const stage = useStageMenu({ tenantId, name, status, onChanged });

  return (
    <>
      <div className="bhead">
        <div>
          <h1 className="bhead__name">{name}</h1>
          <div className="bhead__meta">
            <span className="mono">{tenantId}</span>
            <span aria-hidden="true">·</span>
            <span title={timezone}>{whereOf(timezone)}</span>
            {isProtected ? (
              <Chip tone="plain" title={t('business.protected.title')}>
                {t('business.protected')}
              </Chip>
            ) : null}
            {status === 'paused' ? (
              <Chip tone="warn" title={statusMeaning(t, status)}>
                {t('stage.name.paused')}
              </Chip>
            ) : null}
            {status === 'offboarded' ? (
              <Chip tone="danger" title={statusMeaning(t, status)}>
                {t('stage.name.offboarded')}
              </Chip>
            ) : null}
          </div>
        </div>
        <div className="bhead__actions" role="group" aria-label={t('business.actions.label')}>
          <button
            type="button"
            className="button"
            onClick={support.look}
            disabled={support.busy}
            title={t('support.note', { name })}
          >
            {support.busy ? t('support.opening') : t('support.button')}
          </button>
          {stage.menu}
        </div>
      </div>

      <div className="bhead__notices">
        <SupportLookNotice state={support} />
        {stage.notice}
      </div>

      <StageTrack
        label={t('stage.track.label')}
        steps={stepsOf(t)}
        current={trackIndex(status)}
        nowLabel={t('stage.track.now')}
      />
    </>
  );
}

function stepsOf(t: T): StageStep[] {
  return [
    {
      key: 'onboarding',
      caption: t('stage.track.step', { n: 1 }),
      name: t('stage.name.onboarding'),
      hint: t('stage.hint.onboarding'),
    },
    {
      key: 'importing',
      caption: t('stage.track.step', { n: 2 }),
      name: t('stage.name.importing'),
      hint: t('stage.hint.importing'),
    },
    {
      key: 'live',
      caption: t('stage.track.step', { n: 3 }),
      name: t('stage.name.live'),
      hint: t('stage.hint.live'),
    },
    {
      key: 'online',
      caption: t('stage.track.later'),
      name: t('stage.name.online'),
      hint: t('stage.hint.online'),
    },
  ];
}

/** Where a status sits on the track; -1 for the two states that are off it. */
export function trackIndex(status: string): number {
  switch (status) {
    case 'onboarding':
      return 0;
    case 'importing':
      return 1;
    case 'live':
      return 2;
    default:
      return -1;
  }
}

/**
 * "St Thomas · AST" from `America/St_Thomas`: the city is the zone name's last
 * segment, the abbreviation is the browser's for today (so it says EDT in
 * summer and EST in winter, as a person would). A zone the browser does not
 * know is shown as it was stored rather than hidden — it is a fact worth
 * seeing wrong.
 */
export function whereOf(timezone: string): string {
  const city = (timezone.split('/').pop() ?? timezone).replace(/_/g, ' ');
  try {
    const abbr = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value;
    return abbr === undefined ? city : `${city} · ${abbr}`;
  } catch {
    return timezone;
  }
}
